"use client";

import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useAuthStore } from "@/stores/auth-store";
import { api } from "@/services/client";

const PUBLIC_PATHS = [
  "/auth/sign-in",
  "/auth/sign-up",
  "/auth/verify-email",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/approval-pending"
];

function AuthLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--surface)]">
      <div className="text-sm text-[var(--ui-text-soft)]">Loading...</div>
    </div>
  );
}

export function AuthGate({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const path = usePathname();
  const { hasHydrated, isAuthenticated, status, token, signOut } = useAuthStore();

  const isPublic = path !== null && (PUBLIC_PATHS.includes(path) || path.startsWith("/share/"));
  const shouldShowApp = hasHydrated && (isAuthenticated || isPublic);

  useEffect(() => {
    if (!hasHydrated) return;
    if (path.startsWith("/share/")) return;

    const isPublicPath = PUBLIC_PATHS.includes(path);
    if (!isAuthenticated && !isPublicPath) {
      router.replace("/auth/sign-in");
      return;
    }
    if (isAuthenticated && path.startsWith("/auth/")) {
      if (status === "pending") {
        router.replace("/approval-pending");
      } else {
        router.replace("/app");
      }
    }
    if (isAuthenticated && status === "pending" && path.startsWith("/app")) {
      router.replace("/approval-pending");
    }
  }, [hasHydrated, isAuthenticated, path, router, status]);

  useEffect(() => {
    if (!hasHydrated) return;
    if (!isAuthenticated) return;
    if (!token) {
      signOut();
      router.replace("/auth/sign-in");
      return;
    }
    if (path.startsWith("/share/")) return;
    if (path.startsWith("/auth/")) return;

    void (async () => {
      try {
        await api.auth.me(token);
      } catch (error) {
        const message = error instanceof Error ? error.message.toLowerCase() : "";
        const isAuthFailure =
          message.includes("unauthorized") ||
          message.includes("not authenticated") ||
          message.includes("invalid") ||
          message.includes("expired") ||
          message.includes("401");
        if (isAuthFailure) {
          signOut();
          router.replace("/auth/sign-in");
        } else {
          console.error("Token validation skipped due to transient error:", error);
        }
      }
    })();
  }, [hasHydrated, isAuthenticated, token, path, router, signOut]);

  if (!shouldShowApp) {
    return <AuthLoading />;
  }

  return <>{children}</>;
}
